import React, { useContext, useEffect, useRef, useState } from "react";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import ICopyToDiffCatalogDTO from "../../../models/DTO/ICopyToDiffCatalogDTO";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IOnCopyToDiffCatalogDTO from "../../../models/DTO/IOnCopyToDiffCatalogDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ICatalog from "../../../models/ICatalog";
import INotification from "../../../models/INotification";
import IOrder from "../../../models/IOrder";
import IVendorModel from "../../../models/IVendorModel";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import ValidateStatusEnum from "../../../models/ValidateStatusEnum";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import OwTitle from "../../shared/OwTitle/OwTitle";
import "./CopyToDiffCatalogModal.scss";
import { Socket } from "socket.io-client";

type Props = {
    closeModalFn: () => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    catalogs: ICatalog[];
    orders: IOrder[];
    vendorModel?: IVendorModel;
};

interface IProcessedOrders {
    guid: string;
    status: ValidateStatusEnum;
    message: string;
    sourceMessage?: string;
    targetMessage?: string;
}

export const CopyToDiffCatalogModal: React.FC<Props> = (props) => {
    const [sourceCatalog, setSourceCatalog] = useState<string>("");
    const [targetCatalog, setTargetCatalog] = useState<string>("");
    const [flags, setFlags] = useState({ isValidate: true, didCopy: false });
    const [processedOrders, setProcessedOrders] = useState<IProcessedOrders[]>([]);
    const [btnActive, setBtnActive] = useState(false);
    const socketRef = useRef({ isValidate: true, didCopy: false });
    socketRef.current = flags;

    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    useEffect(() => {
        props.socket.on(EndpointsEnum.OrderOnCopyToDiffCatalog, onCopyToDiffCatalog);
        props.socket.on(EndpointsEnum.Notify, notify);

        return () => {
            props.socket.removeListener(EndpointsEnum.OrderOnCopyToDiffCatalog, onCopyToDiffCatalog);
            props.socket.removeListener(EndpointsEnum.Notify, notify);
        };
    }, []);

    const notify = () => {
        setBtnActive(false);
    };

    const btnClick = () => {
        copyToDiffCatalog();
    };

    const copyToDiffCatalog = () => {
        const orderGuids = props.orders.map((order) => {
            return order.guid;
        });

        const payload: ICopyToDiffCatalogDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuids,
            targetCatalogGuid: targetCatalog,
            sourceCatalogGuid: sourceCatalog,
            isValidate: flags.isValidate,
        };

        if (props.vendorModel) {
            payload.vendorGuid = props.vendorModel.guid;
        }

        props.socket.emit(EndpointsEnum.OrderCopyToDiffCatalog, payload);

        setBtnActive(true);

        console.log(`isValidate: ${flags.isValidate}`);
    };

    const onCopyToDiffCatalog = (data: IOnCopyToDiffCatalogDTO) => {
        console.log(`isValidate: ${flags.isValidate}`);
        if (socketRef.current.isValidate) {
            const notification: INotification = {
                message: "SUCCESSFULLY_VALIDATED_ORDERS",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };

            props.notifyFn(null, notification);

            setFlags({ ...flags, isValidate: false });
        } else {
            const notification: INotification = {
                message: "SUCCESSFULLY_COPIED_ORDERS",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };

            props.notifyFn(null, notification);

            setFlags({ ...flags, didCopy: true });
        }

        console.log(`didCopy: ${flags.didCopy}`);

        setProcessedOrders(data.processedOrders);
        setBtnActive(false);
    };

    const closeModal = () => {
        props.closeModalFn();
    };

    const selectSourceCatalog = (catalog: ICatalog) => {
        setSourceCatalog(catalog.guid);
    };

    const selectTargetCatalog = (catalog: ICatalog) => {
        setTargetCatalog(catalog.guid);
    };

    return (
        <div className={"CopyToDiffCatalogModal p-2"}>
            <div className={"w-100"}>
                <div className={"d-flex justify-content-between"}>
                    <OwTitle leftIconName={"fad fa-copy"} title={"copy_to_diff_catalog"} tag={"h3"} />
                    <div>
                        <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                    </div>
                </div>
            </div>

            <div>
                <div>
                    <OwTitle title={"source"} tag={"h5"} />
                </div>
                <OwSelectOption
                    valueProp={"guid"}
                    titleProp={"name"}
                    fn={selectSourceCatalog}
                    options={props.catalogs}
                    name={"source"}
                    emitObject={true}
                />
            </div>

            <div>
                <div>
                    <OwTitle title={"target"} tag={"h5"} />
                </div>
                <OwSelectOption
                    valueProp={"guid"}
                    titleProp={"name"}
                    fn={selectTargetCatalog}
                    options={props.catalogs}
                    name={"target"}
                    emitObject={true}
                />
            </div>

            <div className={"mt-3"}>
                {processedOrders.map((processedOrder) => {
                    const orderModel = props.orders.find((order) => order.guid === processedOrder.guid);

                    return (
                        <div key={processedOrder.guid} className={"mb-3 d-flex mb-3"}>
                            <div className={"d-flex"}>
                                {orderModel ? <div>{orderModel.name}</div> : null}
                                {processedOrder.status ? (
                                    <div className={"ms-2 me-2"}>
                                        <OwLabel title={processedOrder.status.toLowerCase()} />
                                    </div>
                                ) : null}
                                {processedOrder.message ? (
                                    <div>
                                        <OwLabel title={processedOrder.message.toLowerCase()} />
                                    </div>
                                ) : null}
                            </div>
                            <div className={"ms-2"}>
                                {processedOrder.sourceMessage ? <div>{processedOrder.sourceMessage}</div> : null}
                                {processedOrder.targetMessage ? <div>{processedOrder.targetMessage}</div> : null}
                            </div>
                        </div>
                    );
                })}
            </div>

            {flags.didCopy ? null : (
                <div className="mt-3">
                    <OwButton title={flags.isValidate ? "validate" : "proceed"} fn={btnClick} active={btnActive} disableOnActive={true} />
                </div>
            )}
        </div>
    );
};
